import { useState, useEffect, useRef } from 'react';
import { Video } from '../types';
import { formatViewCount, formatDuration, formatPublishDate } from '../utils/format';
import { trackVideoPlay, trackVideoShare, trackWatchLaterAdd, trackChannelClick } from '../utils/analytics';

interface VideoCardProps {
  video: Video;
  rank: number;
  onPlay?: (video: Video) => void;
  onAddToWatchLater?: (video: Video) => void;
  isInWatchLater?: boolean;
  isSelected?: boolean;
  onChannelClick?: (channelId: string) => void;
  isCompareSelected?: boolean;
  onCompareToggle?: (video: Video) => void;
}

/**
 * Format velocity as rounded K (e.g., +131K/hr)
 * Uses deltaPerHour from API if available and > 0, otherwise calculates from viewCount/time
 */
function formatVelocity(video: Video): string {
  // Prefer delta from API (for time period views), but fall back if 0 or missing
  const velocity = (video.deltaPerHour && video.deltaPerHour > 0)
    ? video.deltaPerHour
    : calculateVelocity(video);

  // Round to nearest thousand
  const velocityK = Math.round(velocity / 1000);

  if (velocityK >= 1000) {
    return `+${(velocityK / 1000).toFixed(1)}M/hr`;
  }
  if (velocityK >= 1) {
    return `+${velocityK}K/hr`;
  }
  // Less than 1K, show actual number
  return `+${Math.round(velocity)}/hr`;
}

/**
 * Calculate velocity from viewCount and publishedAt (fallback)
 */
function calculateVelocity(video: Video): number {
  const publishDate = new Date(video.publishedAt);
  const hoursSincePublish = Math.max(1, (Date.now() - publishDate.getTime()) / (1000 * 60 * 60));
  return video.viewCount / hoursSincePublish;
}

/**
 * Video card component displaying a single video's information
 */
export function VideoCard({
  video,
  rank,
  onPlay,
  onAddToWatchLater,
  isInWatchLater = false,
  isSelected = false,
  onChannelClick,
  isCompareSelected = false,
  onCompareToggle,
}: VideoCardProps) {
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const youtubeUrl = `https://www.youtube.com/watch?v=${video.id}`;
  const channelUrl = `https://www.youtube.com/channel/${video.channelId}`;

  // Scroll into view when selected via keyboard
  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSelected]);

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(youtubeUrl);
      setCopied(true);
      trackVideoShare(video.id);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.open(youtubeUrl, '_blank');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onPlay) {
      e.preventDefault();
      trackVideoPlay(video.id, video.title);
      onPlay(video);
    }
  };

  const handleWatchLater = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackWatchLaterAdd(video.id);
    onAddToWatchLater?.(video);
  };

  const handleChannelClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackChannelClick(video.channelId, video.channelTitle);
    onChannelClick?.(video.channelId);
  };

  return (
    <article
      ref={cardRef}
      className={`video-card ${isSelected ? 'selected' : ''}`}
      style={isSelected ? { outline: '2px solid var(--color-primary)', outlineOffset: '2px' } : undefined}
    >
      <a
        href={youtubeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="video-thumbnail-link"
        onClick={handleClick}
      >
        <div className="video-thumbnail-container">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="video-thumbnail"
            loading="lazy"
          />
          <span className="video-duration">{formatDuration(video.duration)}</span>
          <span className="video-rank">#{rank}</span>
          {onCompareToggle && (
            <button
              className={`compare-select-button ${isCompareSelected ? 'selected' : ''}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompareToggle(video); }}
              aria-pressed={isCompareSelected}
              title={isCompareSelected ? 'Remove from comparison' : 'Select this video to compare'}
            >
              {isCompareSelected ? '✓ Selected' : '⚖ Compare'}
            </button>
          )}
          {onAddToWatchLater && (
            <button
              className={`watch-later-button ${isInWatchLater ? 'added' : ''}`}
              onClick={handleWatchLater}
              title={isInWatchLater ? 'Added to Watch Later' : 'Add to Watch Later'}
            >
              {isInWatchLater ? '✓' : '+'}
            </button>
          )}
          <div className="video-overlay">
            <span className="play-icon">▶</span>
          </div>
        </div>
      </a>

      <div className="video-info">
        <h3 className="video-title">
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={video.title}
            onClick={handleClick}
          >
            {video.title}
          </a>
        </h3>

        {onChannelClick ? (
          <button
            className="video-channel video-channel-btn"
            onClick={handleChannelClick}
          >
            {video.channelTitle}
          </button>
        ) : (
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="video-channel"
          >
            {video.channelTitle}
          </a>
        )}

        <div className="video-meta">
          <span className="video-views">{formatViewCount(video.viewCount)} views</span>
          <span className="video-separator">•</span>
          <span className="video-velocity">{formatVelocity(video)}</span>
          <span className="video-separator">•</span>
          <span className="video-date">{formatPublishDate(video.publishedAt)}</span>
          <button
            className={`share-button ${copied ? 'copied' : ''}`}
            onClick={handleShare}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            {copied ? '✓' : '🔗'}
          </button>
        </div>
      </div>
    </article>
  );
}
